import { FC, useCallback } from 'react';

import { IACUserRequestChannels } from '@/pages/UserAccount/UserRequests/lib';

import { CabinetTemplate, CabinetFilters } from '@/widgets/Templates';
import {
  useCabinetFilter,
  FilterField,
  FilterStatus,
} from '@/widgets/Templates/CabinetTemplate/ui/CabinetFilters/hook/useCabinetFilter';

import { ArtifactType } from '@/shared/types';
import {
  ContentField,
  FormDateRangePicker,
  FormSelect,
  ServiceRowCard,
  ServiceRowCardVariant,
  Typography,
} from '@/shared/ui';

import { Loader } from '@/shared/ui';

import { useArtifactLoader } from '@/hooks/useArtifactLoader';

import { queryKeys } from '@/api/queryKeys';
import { IACUserRequest } from '@/modules/iac/types';

import api from '@/api';

import { asideInformer } from './model/constants';

const filterFields: FilterField[] = [
  {
    filterKey: 'purpose_name',
    type: 'select',
  },
  {
    filterKey: 'periodFrom',
    type: 'date',
  },
  {
    filterKey: 'periodTo',
    type: 'date',
  },
];

export const CriminalRecordSearchAll: FC = () => {
  const { downloadArtifact } = useArtifactLoader(async (artifactType: ArtifactType, id?: string) => {
    return api.iac.downloadUserRequestArtifact(id ?? '', artifactType);
  });

  const loadDataFn = useCallback(() => api.iac.getUserRequests(''), []);

  const { filteredData, onFilter, onReset, selectOptionsMap, dateLimits, status } = useCabinetFilter<IACUserRequest>({
    queryKey: queryKeys.criminalRecords.list(),
    loadDataFn,
    sortByField: 'created_at',
    filterFields,
  });

  const getContent = () => {
    switch (status) {
      case FilterStatus.LOADING:
        return <Loader centered />;
      case FilterStatus.LOAD_ERROR:
        return (
          <ContentField
            icon="hi"
            content={
              <Typography color="graphite" textCenter>
                Наразі у вас немає жодного витягу
                <br />
                Коли ви замовите перший витяг, він зʼявиться тут.
              </Typography>
            }
          />
        );

      case FilterStatus.NO_FILTER_RESULTS:
        return (
          <>
            <CabinetFilters
              inputs={(methods) => (
                <>
                  <FormSelect
                    label="Мета запиту"
                    name="purpose_name"
                    control={methods.control}
                    options={selectOptionsMap['purpose_name']}
                  />
                  <FormDateRangePicker methods={methods} dateLimits={dateLimits} />
                </>
              )}
              onSearch={onFilter}
              onClear={onReset}
            />
            <ContentField
              icon="no_result"
              content={
                <Typography color="graphite" textCenter>
                  За вашим запитом нічого не знайдено.
                </Typography>
              }
            />
          </>
        );

      case FilterStatus.SUCCESS:
      default:
        return (
          <>
            <CabinetFilters
              inputs={(methods) => (
                <>
                  <FormSelect
                    label="Мета запиту"
                    name="purpose_name"
                    control={methods.control}
                    options={selectOptionsMap['purpose_name']}
                  />
                  <FormDateRangePicker methods={methods} dateLimits={dateLimits} />
                </>
              )}
              onSearch={onFilter}
              onClear={onReset}
            />
            {filteredData.map((item) => (
              <ServiceRowCard
                key={item.id}
                status={item.status_decorator ?? item.status}
                data={{
                  id: item.id,
                  title: 'Запит на отримання витягу про наявності судимості',
                  extractDate: item.completed_at,
                  requestDate: item.created_at,
                  requestNumber: item.esn,
                  extractPurpose: item.purpose_name,
                  artifacts: item.artifacts,
                }}
                channel={item.channel}
                channels={IACUserRequestChannels}
                onDownloadExtract={({ id, artifactType }: { id: string; artifactType: ArtifactType }) =>
                  downloadArtifact(artifactType, id)
                }
                onDownloadSignatureFile={({ id, artifactType }: { id: string; artifactType: ArtifactType }) =>
                  downloadArtifact(artifactType, id)
                }
                variant={ServiceRowCardVariant.EXTRACT}
              />
            ))}
          </>
        );
    }
  };

  return (
    <CabinetTemplate title="Витяги" informer={asideInformer}>
      {getContent()}
    </CabinetTemplate>
  );
};
